import React, { useEffect, useState } from 'react';
import { PackageX, AlertCircle, TrendingDown, ChevronRight, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useProductStore from '../../stores/useProductStore';

/**
 * Stock Alerts Widget
 * ويدجت تنبيهات المخزون
 *
 * Displays low stock, out of stock, and slow moving products
 * Shows on dashboard home page as an alert widget
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Clean Component Structure
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-UX-001: Loading States
 * - P010: Error Handling
 */

const StockAlertsWidget = () => {
  const navigate = useNavigate();
  const {
    vehicleInventory,
    fetchMyVehicleInventory,
    loading,
    error
  } = useProductStore();

  const [alerts, setAlerts] = useState({
    lowStock: [],
    outOfStock: [],
    slowMoving: []
  });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadVehicleInventory();
  }, []);

  useEffect(() => {
    if (vehicleInventory && vehicleInventory.length > 0) {
      loadStockAlerts();
    }
  }, [vehicleInventory]);

  const loadVehicleInventory = async () => {
    try {
      await fetchMyVehicleInventory();
    } catch (error) {
      console.error('Failed to load vehicle inventory:', error);
    }
  };

  const loadStockAlerts = () => {
    // Analyze representative's vehicle inventory for alerts
    const lowStock = [];
    const outOfStock = [];
    const slowMoving = [];

    console.log('Vehicle Inventory for alerts:', vehicleInventory);

    vehicleInventory.forEach(item => {
      const quantity = item.quantity || 0;
      const minStock = item.min_stock_level || item.min_stock || 10;

      if (quantity === 0) {
        outOfStock.push({
          id: item.product_id,
          code: item.product_code,
          name: item.product_name,
          category: item.category_name || 'غير مصنف',
          stock: quantity,
          minStock: minStock,
          unit: item.unit || 'وحدة',
          last_sale_date: item.last_sale_date
        });
      } else if (quantity <= minStock) {
        lowStock.push({
          id: item.product_id,
          code: item.product_code,
          name: item.product_name,
          category: item.category_name || 'غير مصنف',
          stock: quantity,
          minStock: minStock,
          unit: item.unit || 'وحدة',
          last_sale_date: item.last_sale_date
        });
      }

      // Check for slow-moving items (no sales in last 60 days)
      if (item.last_sale_date) {
        const lastSaleDate = new Date(item.last_sale_date);
        const daysSinceLastSale = Math.floor((new Date() - lastSaleDate) / (1000 * 60 * 60 * 24));

        if (daysSinceLastSale > 60 && quantity > 0) {
          slowMoving.push({
            id: item.product_id,
            code: item.product_code,
            name: item.product_name,
            category: item.category_name || 'غير مصنف',
            stock: quantity,
            minStock: minStock,
            unit: item.unit || 'وحدة',
            last_sale_date: item.last_sale_date
          });
        }
      }
    });

    setAlerts({
      lowStock,
      outOfStock,
      slowMoving
    });
  };

  const totalAlerts = alerts.lowStock.length + alerts.outOfStock.length + alerts.slowMoving.length;

  const handleViewProducts = (status) => {
    navigate('/products', { state: { filter: status } });
  };

  const handleViewProduct = (productId) => {
    navigate(`/products/${productId}`);
  };

  // Combine all alerts and show top 5
  const allAlerts = [
    ...alerts.outOfStock.map((p) => ({ ...p, type: 'out', priority: 3 })),
    ...alerts.lowStock.map((p) => ({ ...p, type: 'low', priority: 2 })),
    ...alerts.slowMoving.map((p) => ({ ...p, type: 'slow', priority: 1 }))
  ].sort((a, b) => b.priority - a.priority);

  const displayedAlerts = expanded ? allAlerts : allAlerts.slice(0, 5);

  // Show empty state instead of hiding widget
  const hasData = totalAlerts > 0;

  const getAlertConfig = (type) => {
    const configs = {
      out: {
        label: 'نفد المخزون',
        color: 'red',
        bgClass: 'bg-red-100 dark:bg-red-900/30',
        textClass: 'text-red-700 dark:text-red-300',
        icon: PackageX
      },
      low: {
        label: 'قريب من النفاد',
        color: 'yellow',
        bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
        textClass: 'text-yellow-700 dark:text-yellow-300',
        icon: AlertCircle
      },
      slow: {
        label: 'راكد',
        color: 'orange',
        bgClass: 'bg-orange-100 dark:bg-orange-900/30',
        textClass: 'text-orange-700 dark:text-orange-300',
        icon: TrendingDown
      }
    };
    return configs[type] || configs.low;
  };

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                تنبيهات المخزون
              </h3>
              <p className="text-xs text-white/80">
                {totalAlerts} تنبيه
              </p>
            </div>
          </div>
          <div className="text-left flex gap-4">
            <div>
              <div className="text-xs text-white/80">نفد</div>
              <div className="text-lg font-bold text-white">
                {alerts.outOfStock.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-white/80">قريب</div>
              <div className="text-lg font-bold text-white">
                {alerts.lowStock.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-white/80">راكد</div>
              <div className="text-lg font-bold text-white">
                {alerts.slowMoving.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-yellow-600 dark:text-yellow-400">
            {error}
          </div>
        ) : !hasData ? (
          <div className="text-center py-8">
            <div className="bg-green-100 dark:bg-green-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
              <Package className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">
              رائع! المخزون بحالة ممتازة
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              جميع المنتجات متوفرة بكميات مناسبة
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedAlerts.map((product) => {
              const config = getAlertConfig(product.type);
              const Icon = config.icon;

              return (
                <div
                  key={`${product.type}-${product.id}`}
                  onClick={() => handleViewProduct(product.id)}
                  className="bg-white dark:bg-gray-800 rounded-xl p-3 cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-[1.02] border border-yellow-100 dark:border-yellow-900"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`${config.bgClass} p-2 rounded-lg`}>
                        <Icon className={`w-5 h-5 ${config.textClass}`} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {product.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {product.code} • {product.category}
                        </div>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {product.stock} {product.unit}
                      </div>
                      {product.minStock && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          الحد الأدنى: {product.minStock}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${config.bgClass} ${config.textClass}`}>
                      {config.label}
                    </span>
                    {product.last_sale_date && product.type === 'slow' && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        آخر بيع: {new Date(product.last_sale_date).toLocaleDateString('ar-EG')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Expand/Collapse Button */}
            {allAlerts.length > 5 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full py-2 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
              >
                {expanded ? 'عرض أقل' : `عرض المزيد (${allAlerts.length - 5}+)`}
              </button>
            )}

            {/* Action Buttons */}
            <div className="grid grid-cols-3 gap-2 mt-2">
              <button
                onClick={() => handleViewProducts('out')}
                className="flex flex-col items-center gap-1 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg py-2 text-xs font-semibold transition-colors"
              >
                <PackageX className="w-4 h-4" />
                <span>نفد المخزون</span>
              </button>
              <button
                onClick={() => handleViewProducts('low')}
                className="flex flex-col items-center gap-1 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 rounded-lg py-2 text-xs font-semibold transition-colors"
              >
                <AlertCircle className="w-4 h-4" />
                <span>قريب من النفاد</span>
              </button>
              <button
                onClick={() => handleViewProducts('slow')}
                className="flex flex-col items-center gap-1 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded-lg py-2 text-xs font-semibold transition-colors"
              >
                <TrendingDown className="w-4 h-4" />
                <span>راكد</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockAlertsWidget;
